import { XCircleIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';

import { DashedTag, SearchBar } from '@/components/chariot-ui';
import { Dropdown } from '@/components/Dropdown';
import { FormGroup } from '@/components/form/FormGroup';
import { Loader } from '@/components/Loader';
import { useGetAgents } from '@/hooks/useAgents';
import { getOsIcon } from '@/sections/agents/utils/osIcons';

interface AgentSelectorProps {
  selectedAgent: string;
  onAgentChange: (agent: string) => void;
}

/**
 * AgentSelector - Component for selecting Aegis agents for internal capabilities
 *
 * Provides a searchable dropdown to select from available online agents.
 * Selected agent is displayed as a removable tag below the search input.
 */
export function AgentSelector({
  selectedAgent,
  onAgentChange,
}: AgentSelectorProps) {
  const [searchValue, setSearchValue] = useState('');

  const { data: agentResults, status: agentStatus } = useGetAgents({
    filter: {
      search: searchValue,
      status: ['online'],
    },
  });

  const searchOptions = useMemo(() => {
    if (!agentResults) return [];

    return agentResults.slice(0, 50).map(agent => ({
      value: agent.client_id,
      label: `${agent.hostname} (${agent.fqdn}) - ${agent.client_id}`,
      id: agent.client_id,
      os: agent.os,
    }));
  }, [agentResults]);

  const isLoading = agentStatus === 'pending';

  const selectedAgentLabel = useMemo(() => {
    if (!selectedAgent || !agentResults) return '';
    const agent = agentResults.find(a => a.client_id === selectedAgent);
    return agent ? agent.hostname : selectedAgent;
  }, [selectedAgent, agentResults]);

  return (
    <FormGroup label="Select Agent" name="agent-selector">
      <div className="space-y-3">
        <Loader isLoading={isLoading}>
          <Dropdown
            focusType="click"
            menu={{
              items: searchOptions.map(option => ({
                label: option.label,
                icon: getOsIcon(option.os),
                onClick: () => onAgentChange(option.value),
              })),
              emptyState: {
                hide: searchOptions.length > 0 || !searchValue,
                label: isLoading ? 'Loading...' : 'No agents found',
              },
              className: 'max-h-[300px]',
            }}
            asChild
          >
            <div>
              <SearchBar
                size="sm"
                className="h-fit w-full"
                placeholder="Search Agents..."
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>
          </Dropdown>
        </Loader>

        {selectedAgent && (
          <div className="flex items-center gap-1">
            <DashedTag
              icon={<XCircleIcon className="size-5" />}
              className="text-chariot-text-secondary py-1"
              size="sm"
              onClick={() => onAgentChange('')}
              label={selectedAgentLabel}
            />
          </div>
        )}
      </div>
    </FormGroup>
  );
}
